import type { BadgeTone } from '@/components/ui'
import type { LeaveReason, MeetingStatus } from '@/types'

/**
 * One badge per meeting lifecycle state, with fixed tone and a translation key.
 *
 * The mapping lives here and nowhere else. A status rendered as "Ready" on one
 * screen and "Complete" on another makes two screens look like two products.
 */
const MAP: Record<MeetingStatus, { key: string; tone: BadgeTone; dot?: boolean }> = {
  scheduled: { key: 'meetingStatus.scheduled', tone: 'neutral' },
  joining: { key: 'meetingStatus.joining', tone: 'info', dot: true },
  recording: { key: 'meetingStatus.recording', tone: 'danger', dot: true },
  processing: { key: 'meetingStatus.processing', tone: 'info', dot: true },
  ready: { key: 'meetingStatus.ready', tone: 'success' },
  failed: { key: 'meetingStatus.failed', tone: 'danger' },
  skipped: { key: 'meetingStatus.skipped', tone: 'neutral' },
  no_audio: { key: 'meetingStatus.noAudio', tone: 'neutral' },
}

/**
 * Overrides for `status === 'failed'` where `leaveReason` names something
 * other than a Sabha/system failure — the bot sat in the lobby, the host
 * clicked Deny, or the platform could never be reached. All three are
 * warning-toned, not danger: nothing broke on our side.
 *
 * Every other leave reason under `failed` (`error`, `null`) keeps the plain
 * `MAP.failed` entry — those are the unclassified/unexpected cases worth the
 * danger tone.
 */
const FAILED_LEAVE_REASON_OVERRIDES: Partial<Record<LeaveReason, { key: string; tone: BadgeTone }>> = {
  not_admitted: { key: 'meetingStatus.notAdmitted', tone: 'warning' },
  denied: { key: 'meetingStatus.denied', tone: 'warning' },
  join_failed: { key: 'meetingStatus.joinFailed', tone: 'warning' },
  meeting_cancelled: { key: 'meetingStatus.meetingCancelled', tone: 'warning' },
}

/** Pure so the mapping can be pinned by a test without rendering the badge. */
export function resolveMeetingStatusBadge(
  status: MeetingStatus,
  leaveReason?: LeaveReason | null,
): { key: string; tone: BadgeTone; dot?: boolean } {
  const override = status === 'failed' && leaveReason ? FAILED_LEAVE_REASON_OVERRIDES[leaveReason] : undefined
  return override ? { ...override, dot: undefined } : MAP[status]
}
